import cx from "classnames";
import { SpinnerIcon } from "../icons";
import { ComponentProps } from "react";
import { WPElement, Children, cloneElement, useMemo, Fragment } from "@wordpress/element";

type Props = ComponentProps<"button"> & {
	className?: string,
	variant?: "primary" | "secondary" | "white",
	size?: "xs" | "sm" | "md" | "lg" | "xl",
	leftIcon?: WPElement,
	rightIcon?: WPElement,
	width?: string,
	loading?: boolean,
	disableFocus?: boolean // z.B. beim Dropdown
};

function iconCss( type: "left" | "right", size: string ) {
	if ( type == "left" ) {
		if ( size == "sm" ) {
			return "-ml-0.5 mr-2";
		} else if ( size == "md" ) {
			return "-ml-1 mr-2";
		} else {
			return "-ml-1 mr-3";
		}
	} else if ( type == "right" ) {
		if ( size == "sm" ) {
			return "-mr-0.5 ml-2";
		} else if ( size == "md" ) {
			return "-mr-1 ml-2";
		} else {
			return "-mr-1 ml-3";
		}
	}
}

export function buildButton( props: Props ) {
	const size = props.size ? props.size : "md";

	let cl = "whitespace-nowrap";
	if ( props.variant ) {
		cl += " inline-flex items-center justify-center text-center shadow-sm transition hover:ease-in-out duration-300";
	}

	if ( props.width ) {
		cl += " " + props.width;
	} else {
		cl += " w-full md:w-auto";
	}

	if ( props.disableFocus ) {
		// do nothing
	} else {
		cl += " focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ui-primary";
	}

	cl += " rounded-md";

	if ( props.className ) {
		cl += " " + props.className;
	}

	if ( size == "xs" ) {
		cl += " px-2 py-1 text-xs font-medium";
	} else if ( size == "sm" ) {
		cl += " px-3 py-2 text-sm font-medium";
	} else if ( size == "md" ) {
		cl += " px-4 py-2 text-sm font-medium";
	} else if ( size == "lg" ) {
		cl += " px-4 py-2 text-base font-medium";
	} else if ( size == "xl" ) {
		cl += " px-6 py-3 text-base font-medium";
	}

	if ( props.variant == "primary" ) {
		cl += " " + cx(
			"text-white bg-gradient-to-r from-primary-700 to-primary-500",
			"hover:from-primary-500 hover:to-primary-700 active:bg-primary-600",
			"focus:outline-none border border-primary-100",
			"active:bg-gradient-to-r active:from-primary-700 active:to-primary-700"
		);
	} else if ( props.variant == "secondary" ) {
		cl += " text-white border";
		cl += " bg-ui-700 border-ui-700 hover:bg-ui-500 active:bg-ui-900 focus:outline-none";
	} else if ( props.variant == "white" ) {
		cl += " text-ui-700 border";
		cl += " bg-white border-neutral-200 hover:border-neutral-300 active:border-neutral-100 active:bg-neutral-100 focus:outline-none";
	}

	let render = ( children: any ) => children;
	if ( props.leftIcon ) {
		render = ( children: any ) => <><span className={ iconCss( "left", size ) }>{ props.leftIcon }</span>{ children }</>;
	} else if ( props.rightIcon ) {
		render = ( children: any ) => <>{ children }<span className={ iconCss( "right", size ) }>{ props.rightIcon }</span></>;
	}

	if ( props.loading !== undefined ) {
		render = ( children: any ) => <><span className={ iconCss( "left", size ) }><SpinnerIcon></SpinnerIcon></span>{ children }</>;
	}

	return {
		className: cx(
			cl,
			// radix states
			"group",
			"radix-state-open:bg-gray-50 dark:radix-state-open:bg-gray-900",
			"radix-state-on:bg-gray-50 dark:radix-state-on:bg-gray-900",
			"radix-state-instant-open:bg-gray-50 radix-state-delayed-open:bg-gray-50"
		),
		render: render
	}
}

export function Button( props: {
	as?: "button" | "string",
	variant?: "primary" | "secondary" | "white",
	size?: "sm" | "md" | "lg" | "xl",
	className?: string,
	children: any,
	onClick?: any,
	leftIcon?: WPElement,
	rightIcon?: WPElement,
	loading?: boolean,
	width?: string,
	disabled?: boolean
} ) {
	const button = buildButton( props );

	let Tag = "button" as keyof JSX.IntrinsicElements;
	if ( props.as && props.as != "button" ) {
		Tag = props.as as keyof JSX.IntrinsicElements;
	}

	return (
		<Tag
			className={ button.className }
			onClick={ props.onClick }
			disabled={ props.disabled }
		>{ button.render( props.children ) }</Tag>
	)
}

